<script setup lang="ts">
  import { computed, onBeforeMount, ref } from 'vue';

  import type { EmailTemplate, EmailTemplateFolder } from '@stuut/api/models';
  import {
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
  } from '@ui/components/command';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';

  withDefaults(
    defineProps<{
      disabled?: boolean;
      isGenerating?: boolean;
      showHeader?: boolean;
    }>(),
    {
      showHeader: true,
    },
  );

  const emit = defineEmits<{
    (e: 'select', templateId: string): void;
  }>();

  const isLoadingTemplates = ref(true);
  const templates = ref<EmailTemplate[]>([]);
  const folders = ref<EmailTemplateFolder[]>([]);

  const isReady = computed<boolean>(() => !isLoadingTemplates.value);

  const folderName = (folderId: string | null | undefined): string | null => {
    if (!folderId) return null;
    return folders.value.find((f) => f.id === folderId)?.name ?? null;
  };

  const groupedTemplates = computed(() => {
    const groups = new Map<string, EmailTemplate[]>();

    templates.value?.forEach((t) => {
      const key = folderName(t.folder_id) ?? 'Uncategorized';
      const existing = groups.get(key) ?? [];
      existing.push(t);
      groups.set(key, existing);
    });

    return Array.from(groups.entries());
  });

  onBeforeMount(async () => {
    try {
      const results = await Promise.all([
        stuut.emailTemplates.list({ params: { limit: 200 } }),
        stuut.emailTemplateFolders.list(),
      ]);

      templates.value = results[0];
      folders.value = results[1];
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isLoadingTemplates.value = false;
    }
  });
</script>

<template>
  <template v-if="templates?.length">
    <template v-if="showHeader">
      <div class="pt-1.5 pb-1">
        <div class="flex items-center gap-x-2.5 px-2 py-2">
          <div class="w-5 text-center shrink-0">
            <i class="fas far fa-diagram-cells text-primary text-xs" />
          </div>

          <span class="text-xs font-semibold">Generate from template</span>
        </div>
      </div>

      <CommandSeparator />
    </template>

    <CommandInput placeholder="Search templates…" />

    <CommandList class="max-h-55">
      <CommandEmpty>No templates found.</CommandEmpty>

      <CommandGroup v-for="[group, ts] in groupedTemplates" :key="group" :heading="group">
        <CommandItem
          v-for="t in ts"
          :key="t.id"
          :value="t.id"
          class="gap-x-2.5 cursor-pointer"
          :disabled="disabled || isGenerating"
          @select="emit('select', t.id)"
        >
          <i class="fa-regular fa-file-lines text-muted-foreground text-2xs" />
          <span class="truncate">{{ t.name }}</span>
        </CommandItem>
      </CommandGroup>
    </CommandList>
  </template>

  <div
    v-else-if="!isReady"
    class="px-3 py-2 text-2xs text-muted-foreground flex items-center gap-2"
  >
    <LoadingSpinner class="text-xs" />
    <span>Loading templates…</span>
  </div>

  <div v-else class="px-3 py-2 text-2xs text-muted-foreground">
    No templates yet —
    <RouterLink class="text-blue hover:underline" :to="{ name: 'playbook.templates.email' }">
      create one
    </RouterLink>
  </div>
</template>

<style scoped></style>
